import React, { useState, useEffect, useRef } from 'react'
import { ControlledFunctionalTag } from 'transfix-form/components'
import ControlledCheckboxGroupInput from 'transfix-form/components/ControlledCheckboxGroupInput'
import ControlledTextField from 'transfix-form/components/ControlledTextInput'
import CloseIcon from 'transfix-icons/build/CloseIcon'
import { Button } from 'transfix-ui/components/Button'
import { IconButton } from 'transfix-ui/components/IconButton'
import { ClickAwayListener, makeStyles, Typography, Box } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { IScraperJobConfigurationFragment } from 'apps/spot-quote-control-panel/graphql/ScraperJobConfigurationFragment'
import { useDisableAutobiddersMessage } from 'apps/spot-quote-control-panel/hooks/useDisableAutobiddersMessage'
import { useUpdateScraperForm } from 'apps/spot-quote-control-panel/hooks/useUpdateScraperForm'
import { useUpdateScraperJob } from 'apps/spot-quote-control-panel/hooks/useUpdateScraperJob'
import { TScraperJobConfigurationUpdateSchema } from 'apps/spot-quote-control-panel/schema/ScraperJobConfigurationUpdateSchema'
import { EditIcon } from 'transfix-icons'
import {
  IFunctionalTagOption,
  TAG_SIZE,
  TAG_STYLE,
  Toast,
  useToastState,
  InlineMessage,
  INLINE_MESSAGE_VARIANTS,
} from 'transfix-ui'
import { SidePanelHistorySection } from '../SidePanelHistorySection'
import { ControlledDemoModeAutocomplete } from './ControlledDemoModeAutocomplete'

interface IUpdateScraperFormProps {
  jobConfig: IScraperJobConfigurationFragment
  setFocusedJob: React.Dispatch<React.SetStateAction<IScraperJobConfigurationFragment | undefined>>
}

const disableOptions = [
  { id: 'Password Reset', label: 'Password Reset' },
  { id: 'Page/TMS Failures', label: 'Page/TMS Failures' },
  { id: 'TMS Changes', label: 'TMS Changes' },
  { id: 'Other', label: 'Other' },
]

const editableTagOptions = [
  { value: 'Enabled', label: 'Enabled', style: TAG_STYLE.info },
  { value: 'Disabled', label: 'Disabled', style: TAG_STYLE.dark },
]

const useStyles = makeStyles(theme => ({
  filtersContainer: {
    position: 'absolute',
    right: 0,
    zIndex: 2,
    backgroundColor: brandColors.white,
    [theme.breakpoints.up('md')]: {
      width: 320,
    },
    overflowY: 'scroll',
    height: '100vh',
  },
  title: {
    padding: theme.spacing(3, 3, 1, 3),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  buttons: { display: 'flex', flexFlow: 'row-reverse' },
  jobSection: {
    margin: theme.spacing(1, 3),
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  historySection: {
    margin: theme.spacing(1, 3),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  section: { gap: theme.spacing(1), margin: theme.spacing(1, 3) },
  sectionTitle: {
    color: brandColors.coolGray5,
    fontWeight: 'bold',
  },
  sectionHeader: {
    marginBottom: theme.spacing(1),
  },
}))

export const UpdateAutobidderConfigForm = ({
  jobConfig,
  setFocusedJob,
}: IUpdateScraperFormProps) => {
  const classes = useStyles()
  const { loading: updateLoading, update } = useUpdateScraperJob()
  const [updateScraperJobResults, setUpdateScraperJobResults] = useState<
    IScraperJobConfigurationFragment[]
  >([])
  const [configUpdatedAt, setConfigUpdatedAt] = useState<string | null>(null)
  const [showDisableChecklist, setShowDisableChecklist] = useState<boolean>(false)
  const [allowEnableSubmission, setAllowEnableSubmission] = useState<boolean>(false)
  const [allowDemoModeSubmission, setAllowDemoModeSubmission] = useState<boolean>(true)
  const [allowQueueNameSubmission, setAllowQueueNameSubmission] = useState<boolean>(false)
  const mountRef = useRef(false)

  const {
    control,
    formState: { dirtyFields },
    handleSubmit,
    setValue,
    watch,
  } = useUpdateScraperForm({
    defaultValues: {
      enabled: jobConfig.isEnabled,
      demoMode: jobConfig.demoMode,
      // jobQueueName: jobConfig.jobQueueName,
    },
  })

  const formComment = watch('comment')
  const formDemoMode = watch('demoMode')
  const formJobQueueName = watch('jobQueueName')

  const { disabledMessage, disabledTitle, updateDisabledMessage } = useDisableAutobiddersMessage({
    updateScraperJobResults,
    configUpdatedAt,
    message: '',
    title: '',
  })

  // each time we disable an autobidder config, we want to update the message displaying
  // when/why/by whom the bidder was disabled
  useEffect(() => {
    updateDisabledMessage(updateScraperJobResults)
  }, [updateScraperJobResults, updateDisabledMessage])

  // this runs when the user clicks to disable and then checks/unchecks reasons why
  // it makes sure that the user can not submit the form without having a reason checked
  useEffect(() => {
    if (formComment && formComment.length > 0) {
      setAllowEnableSubmission(true)
    } else {
      setAllowEnableSubmission(false)
    }
  }, [formComment, formDemoMode])

  useEffect(() => {
    if (formDemoMode !== null) {
      setAllowDemoModeSubmission(!allowDemoModeSubmission)
    }
  }, [formDemoMode])

  const { closeToast, multiLine, openToast, toastMessage, toastOpen, toastType } = useToastState()

  const updateScraperJobs = async (formValues: TScraperJobConfigurationUpdateSchema) => {
    try {
      const { updateScraperJobErrors, updateScraperJobResults, updatedAt } = await update(
        formValues,
        jobConfig.jobUuid
      )

      if (updateScraperJobErrors && updateScraperJobErrors.length > 0) {
        const errors = updateScraperJobErrors.map(error => error.message).join(', ')
        openToast({
          toastMessage: `Errors applying updates: ${errors}. Contact FA Engineering`,
          toastType: 'alert',
        })
      } else {
        openToast({
          toastMessage: 'Successfully updated autobidder configuration',
          toastType: 'default',
        })
      }

      if (updatedAt) {
        setConfigUpdatedAt(updatedAt)
      }

      setUpdateScraperJobResults(updateScraperJobResults)

      if (showDisableChecklist) {
        setShowDisableChecklist(false)
      }
    } catch (error) {
      openToast({
        toastMessage: 'There was an error updating this configuration, contact FA Engineering',
        toastType: 'alert',
      })
    }
  }

  const onSubmitEnabled = (formValues: TScraperJobConfigurationUpdateSchema) => {
    formValues.demoMode = null
    formValues.jobQueueName = null
    updateScraperJobs(formValues)
  }

  const onSubmitDemoMode = (formValues: TScraperJobConfigurationUpdateSchema) => {
    formValues.enabled = null
    formValues.jobQueueName = null
    updateScraperJobs(formValues)
  }

  const onSubmitQueueName = (formValues: TScraperJobConfigurationUpdateSchema) => {
    formValues.enabled = null
    formValues.demoMode = null
    updateScraperJobs(formValues)
  }

  const submitEnabledHandler = () => {
    handleSubmit(onSubmitEnabled)()
  }

  const submitDemoModeHandler = () => {
    handleSubmit(onSubmitDemoMode)()
    setAllowDemoModeSubmission(false)
  }

  const submitQueueNameHandler = () => {
    handleSubmit(onSubmitQueueName)()
    setAllowQueueNameSubmission(false)
    setFocusedJob({ ...jobConfig, jobQueueName: formJobQueueName })
  }

  useEffect(() => {
    setValue('enabled', jobConfig.isEnabled)
    if (jobConfig.isEnabled && mountRef.current === true) {
      submitEnabledHandler()
      setShowDisableChecklist(true)
    } else if (jobConfig.isEnabled) {
      setShowDisableChecklist(true)
    }
    // this ensures that this hook does not run on the first render
    mountRef.current = true
  }, [jobConfig.isEnabled])

  const closePanelHandler = () => {
    setFocusedJob(undefined)
  }

  const enabledTagHandler = (selectedOption: IFunctionalTagOption<string>) => {
    if (selectedOption.value === 'Enabled') {
      setFocusedJob({ ...jobConfig, isEnabled: true })
    } else {
      setShowDisableChecklist(true)
      setFocusedJob({ ...jobConfig, isEnabled: false })
    }
  }

  const editJobQueueHandler = () => {
    setAllowQueueNameSubmission(true)
  }

  const cancelUpdateEnabledHandler = () => {
    const enabled = !jobConfig.isEnabled
    setFocusedJob({ ...jobConfig, isEnabled: enabled })
  }

  const cancelUpdateDemoModeHandler = () => {
    const demoMode = !jobConfig.demoMode
    setValue('demoMode', demoMode)
    setFocusedJob({ ...jobConfig, demoMode })
  }

  const cancelUpdateQueueNameHandler = () => {
    setAllowQueueNameSubmission(false)
  }

  const warningMessage =
    'Disabling this job will stop price request records from being created. If records are still desired, implement a Do Not Bid rule instead'

  const enabledTagLabel = jobConfig.isEnabled ? 'Enabled' : 'Disabled'

  return (
    <form className={classes.filtersContainer}>
      <ClickAwayListener onClickAway={closePanelHandler}>
        <div>
          <Toast
            variation={toastType}
            open={toastOpen}
            onClose={closeToast}
            message={toastMessage}
            multiLine={multiLine}
            duration={null}
          />
          <div className={classes.title}>
            <Typography variant='h3'>{jobConfig.tmsName}</Typography>
            <IconButton
              color='tertiary'
              Icon={CloseIcon}
              size='large'
              onClick={closePanelHandler}
            />
          </div>
          <section className={classes.jobSection}>
            <div className={classes.sectionTitle}>Job</div>
            <div>
              {jobConfig.jobType} | {jobConfig.description}
            </div>
            <div>{jobConfig.shipperApplication.name}</div>
            <ControlledFunctionalTag
              control={control}
              name='enabled'
              FunctionalTagProps={{
                value: enabledTagLabel,
                size: TAG_SIZE.small,
                options: editableTagOptions,
                onSelect: enabledTagHandler,
              }}
            />
            {showDisableChecklist && jobConfig.isEnabled === false && (
              <div>
                <InlineMessage
                  title='Warning'
                  message={warningMessage}
                  type={INLINE_MESSAGE_VARIANTS.WARNING}
                />
                <ControlledCheckboxGroupInput
                  control={control}
                  name='comment'
                  label='Why is this job being disabled?'
                  options={disableOptions}
                />
                <div className={classes.buttons}>
                  <Button
                    color='primary'
                    size='large'
                    onClick={submitEnabledHandler}
                    disabled={!allowEnableSubmission}>
                    Save Status
                  </Button>
                  <Button color='tertiary' onClick={cancelUpdateEnabledHandler}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}
            {disabledTitle !== '' && dirtyFields?.comment === true && (
              <InlineMessage title={disabledTitle} message={disabledMessage} />
            )}
          </section>
          <section className={classes.jobSection}>
            <Typography className={classes.sectionHeader} variant='subtitle1'>
              Mode
            </Typography>
            <div className={classes.sectionTitle}>
              In live mode, price requests and bids will be submitted to shippers.
            </div>
            <Box mb={1} mt={1}>
              <ControlledDemoModeAutocomplete
                control={control}
                name='demoMode'
                defaultValue={{ demoMode: jobConfig.demoMode }}
                jobConfig={jobConfig}
                setFocusedJob={setFocusedJob}
              />
            </Box>
            {allowDemoModeSubmission && (
              <div className={classes.buttons}>
                <Button color='primary' size='large' onClick={submitDemoModeHandler}>
                  Save Status
                </Button>
                <Button color='tertiary' onClick={cancelUpdateDemoModeHandler}>
                  Cancel
                </Button>
              </div>
            )}
          </section>
          <section className={classes.jobSection}>
            <Typography className={classes.sectionHeader} variant='subtitle1'>
              Queue Name
            </Typography>
            <div className={classes.sectionTitle}>This is the queue name the scraper runs on.</div>
            {!allowQueueNameSubmission && (
              <Box mb={1} mt={1} display='flex' onClick={editJobQueueHandler}>
                <div>{jobConfig.jobQueueName} </div>
                <div>
                  <EditIcon color='skyBlue5' size='large' />
                </div>
              </Box>
            )}
            {allowQueueNameSubmission && (
              <div>
                <Box mb={1} mt={1}>
                  <ControlledTextField
                    control={control}
                    name='jobQueueName'
                    defaultValue={jobConfig.jobQueueName}
                  />
                </Box>
                <div className={classes.buttons}>
                  <Button color='primary' size='large' onClick={submitQueueNameHandler}>
                    Save Status
                  </Button>
                  <Button color='tertiary' onClick={cancelUpdateQueueNameHandler}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </section>
          <section className={classes.historySection}>
            <SidePanelHistorySection jobId={jobConfig.jobUuid} updateLoading={updateLoading} />
          </section>
        </div>
      </ClickAwayListener>
    </form>
  )
}
